import { useEffect, useRef } from 'react'

/**
 * Call `poll` while `active`, with exponential backoff from `firstMs` (doubling, capped at `maxMs`), giving up
 * once `giveUpMs` have passed — a stuck condition must not keep an open tab polling forever. Restarts from
 * `firstMs` whenever `active` turns true again; stops the moment it turns false or the component unmounts.
 */
export function usePollWhile(active: boolean, poll: () => void, { firstMs, maxMs, giveUpMs }: { firstMs: number; maxMs: number; giveUpMs: number }): void {
    // The latest poll without restarting the schedule on every render.
    const pollRef = useRef(poll)
    pollRef.current = poll
    useEffect(() => {
        if (!active) return
        let delay = firstMs
        let waited = 0
        let timer: ReturnType<typeof setTimeout> | undefined
        const schedule = () => {
            if (waited >= giveUpMs) return
            timer = setTimeout(() => {
                waited += delay
                delay = Math.min(delay * 2, maxMs)
                pollRef.current()
                schedule()
            }, delay)
        }
        schedule()
        return () => clearTimeout(timer)
    }, [active, firstMs, maxMs, giveUpMs])
}
